import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn, formatNumber } from '@/lib/utils';
import { Button, Select } from './primitives';
import { TableSkeleton } from './surfaces';

/*
 * Tableau de données.
 *
 * L'écran de recherche et l'écran des lots affichent les mêmes informations
 * sous la même forme ; les décrire deux fois garantirait qu'ils divergent. Ce
 * composant impose donc la densité, l'alignement des colonnes numériques et le
 * comportement de survol — trois choses qu'on ne veut pas voir varier d'un
 * écran à l'autre.
 */

export interface Column<T> {
  readonly key: string;
  readonly header: ReactNode;
  readonly render: (row: T) => ReactNode;
  readonly align?: 'left' | 'right' | 'center';
  readonly width?: string;
  /** Masque la colonne sous 1024 px : les écrans de bureau restent prioritaires. */
  readonly hideOnNarrow?: boolean;
}

export interface DataTableProps<T> {
  columns: readonly Column<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string;
  loading?: boolean;
  empty?: ReactNode;
  onRowClick?: (row: T) => void;
  /** Ligne mise en évidence — le document ouvert dans la visionneuse. */
  activeRowKey?: string | null;
  className?: string;
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading,
  empty,
  onRowClick,
  activeRowKey,
  className,
}: DataTableProps<T>): React.JSX.Element {
  if (loading) {
    return <TableSkeleton rows={7} columns={columns.length} />;
  }

  if (rows.length === 0) {
    return <>{empty}</>;
  }

  return (
    <div className={cn('w-full overflow-x-auto', className)}>
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-line">
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                style={column.width ? { width: column.width } : undefined}
                className={cn(
                  'px-4 py-2.5 text-2xs font-semibold tracking-wide text-ink-faint uppercase',
                  column.align === 'right' && 'text-right',
                  column.align === 'center' && 'text-center',
                  column.align !== 'right' && column.align !== 'center' && 'text-left',
                  column.hideOnNarrow && 'hidden lg:table-cell',
                )}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="divide-y divide-line-subtle">
          {rows.map((row) => {
            const key = rowKey(row);
            const active = activeRowKey === key;

            return (
              <tr
                key={key}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onKeyDown={
                  onRowClick
                    ? (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onRowClick(row);
                        }
                      }
                    : undefined
                }
                tabIndex={onRowClick ? 0 : undefined}
                // Le rôle `button` est délibéré : une ligne cliquable doit être
                // annoncée comme actionnable, pas comme une simple cellule.
                role={onRowClick ? 'button' : undefined}
                className={cn(
                  'transition-colors duration-100',
                  onRowClick && 'cursor-pointer hover:bg-hover focus:bg-hover focus:outline-none',
                  active && 'bg-accent-soft hover:bg-accent-soft',
                )}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cn(
                      'px-4 py-2.5 align-middle text-ink',
                      column.align === 'right' && 'text-right tabular',
                      column.align === 'center' && 'text-center',
                      column.hideOnNarrow && 'hidden lg:table-cell',
                    )}
                  >
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export interface PaginationProps {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  className?: string;
}

export function Pagination({
  page,
  pageSize,
  total,
  totalPages,
  onPageChange,
  onPageSizeChange,
  className,
}: PaginationProps): React.JSX.Element | null {
  if (total === 0) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 border-t border-line-subtle px-4 py-2.5',
        className,
      )}
    >
      <p className="text-xs text-ink-faint">
        <span className="tabular font-medium text-ink-muted">
          {formatNumber(from)}–{formatNumber(to)}
        </span>{' '}
        sur <span className="tabular font-medium text-ink-muted">{formatNumber(total)}</span>
      </p>

      <div className="flex items-center gap-2">
        {onPageSizeChange && (
          <Select
            size="sm"
            value={pageSize}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
            aria-label="Nombre de lignes par page"
            className="w-auto"
          >
            {[25, 50, 100, 200].map((size) => (
              <option key={size} value={size}>
                {size} / page
              </option>
            ))}
          </Select>
        )}

        <div className="flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            icon={<ChevronLeft className="size-4" />}
            aria-label="Page précédente"
          />
          <span className="tabular px-2 text-xs text-ink-muted">
            {page} / {totalPages}
          </span>
          <Button
            size="sm"
            variant="ghost"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            icon={<ChevronRight className="size-4" />}
            aria-label="Page suivante"
          />
        </div>
      </div>
    </div>
  );
}
